import React, { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import FileIcon from 'images/v5/icons/file-04.svg';
import FileHeartSvg from 'images/v5/icons/file-heart-03.svg';
import FolderIcon from 'images/v5/icons/folder.svg';
import HelpCircleSvg from 'images/v5/icons/help-circle.svg';
import StarsSvg from 'images/v5/icons/stars-01.svg';
import Bar from 'layout/atoms/Bar';
import Button from 'layout/atoms/Button/Button';
import Caption from 'layout/atoms/Caption';
import Separator from 'layout/atoms/Separator';
import Subtitle from 'layout/atoms/Subtitle';
import Grid from 'layout/molecules/Grid/Grid';
import Sticky from 'layout/molecules/Sticky';
import FoldersTree from 'layout/organisms/FoldersTree/FoldersTree';
import Submenu from 'organisms/Submenu/Submenu';
import { clearBreadcrumbs, openHelpAgent, openPageGuide, setBreadcrumbs } from 'store/layoutSlice';
import { fetchRootFolder, fetchRootFolders, selectFolderName, selectFoldersTree, } from 'store/specificationsFoldersSlice';
import Dashboard from 'templates/Dashboard/Dashboard';
import { getMenuLabel } from 'utils/getMenuItem';
import AllSpecifications from './AllSpecifications';
import Filters from './Filters/Filters';
import FolderCreator from './FolderCreator/FolderCreator';
import FolderView from './FolderView';
import HeaderAside from './HeaderAside/HeaderAside';
function SpecificationsDashboard() {
    var _a;
    const { t } = useTranslation(['specification', 'exceptions', 'common']);
    const dispatch = useDispatch();
    const location = useLocation();
    const layout = useSelector((state) => state.layout);
    const rootFolder = useSelector((state) => state.specificationsFolders.rootFolder);
    const refetchFlags = useSelector((state) => state.specificationsFolders.refetchFlags);
    const isMyFiles = location.pathname === '/specifications/my-files';
    const isAllFiles = location.pathname === '/specifications/all-files';
    const isFilesView = isMyFiles || isAllFiles;
    const isFolderView = location.pathname.includes('/specifications/folders/');
    const folderId = isFolderView
        ? location.pathname.replace('/specifications/folders/', '')
        : rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId;
    const foldersTree = useSelector(selectFoldersTree);
    const folderName = useSelector((state) => selectFolderName(state, folderId));
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (refetchFlags.fetchList && (rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId)) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, refetchFlags.fetchList, rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    const defaultLinks = [
        {
            id: 'all_folders',
            to: `/specifications/folders/${rootFolder.rootFolderId}`,
            text: t('all_folders'),
            icon: React.createElement(FolderIcon, null),
        },
        {
            id: 'all_files',
            to: '/specifications/all-files',
            text: t('all_files'),
            icon: React.createElement(FileIcon, null),
        },
        {
            id: 'my_files',
            to: '/specifications/my-files',
            text: t('my_files'),
            icon: React.createElement(FileHeartSvg, null),
        },
    ];
    const folderBreadcrumbs = useMemo(() => {
        if (!foldersTree)
            return [];
        const breadcrumbs = [];
        let searchId = folderId;
        while (searchId) {
            // eslint-disable-next-line no-loop-func
            const folder = foldersTree.find((f) => f.id === searchId);
            if (!folder)
                break;
            breadcrumbs.unshift({
                id: folder.id,
                to: `/specifications/folders/${folder.id}`,
                name: folder.name,
            });
            searchId = folder.parentId;
        }
        return breadcrumbs;
    }, [foldersTree, folderId]);
    const displayedFolderBreadcrumbs = useMemo(() => {
        if (folderBreadcrumbs.length <= 2) {
            return folderBreadcrumbs;
        }
        const current = folderBreadcrumbs[folderBreadcrumbs.length - 1];
        const parent = folderBreadcrumbs[folderBreadcrumbs.length - 2];
        return [{ id: 'ellipsis', to: parent.to, name: '...' }, current];
    }, [folderBreadcrumbs]);
    useEffect(() => {
        const specificationsLabel = getMenuLabel('/specifications', t);
        if (location.pathname === '/specifications/my-files') {
            dispatch(setBreadcrumbs([
                { to: '/specifications', name: specificationsLabel },
                { to: '/specifications/my-files', name: t('my_files') },
            ]));
        }
        else if (location.pathname === '/specifications/all-files') {
            dispatch(setBreadcrumbs([
                { to: '/specifications', name: specificationsLabel },
                { to: '/specifications/all-files', name: t('all_files') },
            ]));
        }
        else if (location.pathname.includes('/specifications/folders/')) {
            dispatch(setBreadcrumbs([
                { to: '/specifications', name: specificationsLabel },
                ...displayedFolderBreadcrumbs,
            ]));
        }
        else {
            dispatch(setBreadcrumbs([{ to: '/specifications', name: specificationsLabel }]));
        }
        return () => {
            dispatch(clearBreadcrumbs());
        };
    }, [dispatch, t, location.pathname, displayedFolderBreadcrumbs]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Dashboard, { headerMain: 
            // eslint-disable-next-line no-nested-ternary
            isFilesView ? React.createElement(HeaderAside, null) : isFolderView ? React.createElement(HeaderAside, { folders: true }) : null, headerAside: React.createElement(React.Fragment, null,
                React.createElement(Button, { color: "inverse", shape: "squared", leftIcon: React.createElement(StarsSvg, null), onClick: () => dispatch(openHelpAgent()) }),
                React.createElement(Button, { color: "inverse", shape: "squared", leftIcon: React.createElement(HelpCircleSvg, null), onClick: () => dispatch(openPageGuide('specifications')) })), asideMenu: React.createElement(Sticky, null,
                React.createElement(Grid, { gap: 0 },
                    React.createElement(Grid, { vp: 16, hp: 12, gap: 4 },
                        React.createElement(Bar, { height: 24, hp: 8 },
                            React.createElement(Caption, { color: "subtle" }, t('common:QUICK_ACCESS'))),
                        React.createElement(Submenu, { groups: [{ id: 'defaultLinks', items: defaultLinks }] })),
                    React.createElement(Separator, null),
                    React.createElement(Grid, { hp: 12 },
                        React.createElement(FoldersTree, { rootFolderId: rootFolder.rootFolderId, folders: foldersTree, withMenu: true })))) },
            React.createElement(Grid, { vp: 16, hp: 20, gap: 24 },
                React.createElement(Bar, { aside: 
                    // eslint-disable-next-line no-nested-ternary
                    isFilesView ? React.createElement(Filters, null) : isFolderView ? React.createElement(Filters, { folders: true }) : null }, 
                // eslint-disable-next-line no-nested-ternary
                isFilesView ? (React.createElement(Subtitle, { size: "s" }, isAllFiles ? t('all_files') : t('my_files'))) : isFolderView ? (React.createElement(Subtitle, { size: "s" }, folderId === rootFolder.rootFolderId ? t('all_folders') : folderName)) : null),
                // eslint-disable-next-line no-nested-ternary
                isFilesView ? (React.createElement(AllSpecifications, { key: location.pathname, allFiles: isAllFiles })) : isFolderView ? (React.createElement(FolderView, { folderId: folderId })) : ((rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId) && (React.createElement(Navigate, { to: `/specifications/folders/${rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId}`, replace: true }))))),
        ((_a = layout.showModals) === null || _a === void 0 ? void 0 : _a.some((modal) => modal.name === 'folderCreator')) && (React.createElement(FolderCreator, { parentFolder: folderId }))));
}
export default memo(SpecificationsDashboard);
